import {
  fmtGain,
  fmtLevel,
  fmtPercent,
  fmtPrice,
  fmtQuantity,
  fmtR,
  fmtRupees,
  fmtSignedPct,
  fmtSignedR,
  fmtSignedRupees,
  fmtSize,
} from '@alphadesk/ontology/primitives.ts';
import {
  daysBetween,
  daysSince,
  istClock,
  istWeekday,
  spanSaid,
  todayInIst,
  weekdaySaid,
  weekdayYearSaid,
} from '@alphadesk/ontology/when.ts';

export {
  istClock,
  istDayMonth,
  istStamp,
  istWeekday,
  monthSaid,
  monthYearSaid,
  spanSaid,
  todayInIst,
} from '@alphadesk/ontology/when.ts';
export {
  fmtGain,
  fmtLevel,
  fmtPercent,
  fmtPrice,
  fmtQuantity,
  fmtR,
  fmtRupees,
  fmtSignedPct,
  fmtSignedR,
  fmtSignedRupees,
  fmtSize,
};

/** Of a fraction of a whole, where `fmtSignedPct` takes one already in percent. */
export const fmtSignedShare = (fraction: number, places?: number): string =>
  fmtSignedPct(fraction * 100, places);

export const fmtWeekdayClock = (iso: string) => `${istWeekday(iso)} ${istClock(iso)}`;

/** "10:02" today, "Mon 10:02" on another day. */
export const fmtClockOf = (iso: string, now: Date): string =>
  todayInIst(new Date(iso)) === todayInIst(now) ? istClock(iso) : fmtWeekdayClock(iso);

/** Whether a set of prices needs its paise, so the set is never spelt two ways. */
export const paiseOf = (...prices: (number | null | undefined)[]): boolean =>
  prices.some((one) => one != null && !Number.isInteger(one));

export function fmtDivider(at: string, today: string): string {
  const day = todayInIst(new Date(at));
  return day.slice(0, 4) === today.slice(0, 4) ? weekdaySaid(day) : weekdayYearSaid(day);
}

export function fmtAge(day: string, now: Date): string {
  const days = daysSince(day, todayInIst(now));
  return days === 0 ? 'today' : `${days}d`;
}

export const fmtAgo = (at: string, now: Date): string => `${spanSaid(at, now)} ago`;

export const fmtAhead = (day: string, today: string): string => `${daysBetween(today, day)}d`;

type Drift = 'up' | 'down' | 'flat';

export function driftOf(value: number | null | undefined): Drift {
  if (value === null || value === undefined || value === 0) return 'flat';
  return value > 0 ? 'up' : 'down';
}

export const DRIFT_TEXT: Record<Drift, string> = {
  up: 'text-up',
  down: 'text-down',
  flat: 'text-ink-muted',
};

/** `DRIFT_TEXT` as a plot's fill. */
export const DRIFT_FILL: Record<Drift, string> = {
  up: 'var(--color-up)',
  down: 'var(--color-down)',
  flat: 'var(--color-ink-muted)',
};

// Axis labels only; a figure a member reads P/L off is spelt in full.
export function fmtCompactRupees(value: number): string {
  const sign = value < 0 ? '−' : '';
  const size = Math.abs(value);
  if (size >= 1e7) return `${sign}₹${(size / 1e7).toFixed(size >= 1e8 ? 0 : 1)}Cr`;
  if (size >= 1e5) return `${sign}₹${(size / 1e5).toFixed(size >= 1e6 ? 0 : 1)}L`;
  if (size >= 1e3) return `${sign}₹${(size / 1e3).toFixed(size >= 1e4 ? 0 : 1)}k`;
  return `${sign}₹${Math.round(size)}`;
}
